
import { Options, Vue, prop } from "vue-class-component";
import { PickupDashboardController } from "@/app/ui/controllers/PickupDashboardController";
import PaginationV3 from "@/app/ui/components/pagination-v3/index.vue";
import { Pagination } from "@/domain/entities/Pagination";
import TableLazyLoad from "@/app/ui/components/lazy-load/table-lazy-load.vue";
import EmptyList from "@/app/ui/components/empty-list/index.vue";

class Props {
  data = prop<Array<any>>({ default: [], type: Array });
  columns = prop<Array<any>>({ default: () => [], type: Array });
  sortBy = prop<string>({ default: "sender_name", type: String });
  sortDir = prop<string>({ default: "asc", type: String });
  startIndex = prop<number>({ default: 0, type: Number });
  pagination = prop<Pagination>({
    default: () => new Pagination(1, 10, 0),
    type: Object
  });
  renderedData = prop<number>({ default: 0, type: Number });
  isLoading = prop<boolean>({ default: false, type: Boolean });
  isFiltering = prop<boolean>({ default: false, type: Boolean });
  isCheckboxDisabledFn = prop<Function>({
    default: (/* item: any */) => false,
    type: Function
  });
  heightOffset = prop<number>({ default: 0, type: Number });
}

@Options({ components: { PaginationV3, TableLazyLoad, EmptyList } })
export default class TableDetail extends Vue.with(Props) {
  get isExpanded() {
    return PickupDashboardController.isSumaryDetailExpanded;
  }

  onSort(key: string) {
    this.$emit("sort", key);
  }

  onSetPage(p: number) {
    this.$emit("set-page", p);
  }

  onRowTransfer(item: any) {
    this.$emit("row-transfer", item);
  }

  computeHeadClass(col: any) {
    const base = "px-4 py-2 sticky top-0 bg-white z-10";
    return `${base} ${col.styleHead || col.headClass || ""}`.trim();
  }
  headIsSortable(col: any) {
    return Boolean(col.sortingKey || (col.sortable && col.key));
  }

  headWrapperClass(col: any) {
    const base = "flex items-center gap-2";
    const isCenter =
      String(col.styleHead || col.headClass || "").includes("text-center") ||
      col.key === "actionable_column";
    return isCenter ? `${base} justify-center` : base;
  }

  bodyWrapperClass(col: any) {
    const base = "flex items-center w-full";
    const isCenter =
      String(col.styleHead || col.headClass || "").includes("text-center") ||
      String(col.styleBody || col.cellClass || "").includes("text-center") ||
      String(col.styleCustom || "").includes("justify-center") ||
      col.key === "actionable_column";
    return isCenter ? `${base} justify-center` : base;
  }

  rowKey(item: any, i: number) {
    return `row-${item.courier_id || item.sender_name || i}`;
  }

  isSelected(item: any) {
    return Boolean(item.is_checked);
  }

  isCheckboxDisabled(item: any) {
    const fn = (this as any).isCheckboxDisabledFn as (i: any) => boolean;
    if (typeof fn === "function") return fn(item);
    return false;
  }

  toggleSelect(item: any) {
    this.$emit("select-toggle", item);
  }

  get isAllSelected() {
    const items = this.data || [];
    if (!items.length) return false;
    const eligible = items.filter((d: any) => !this.isCheckboxDisabled(d));
    if (eligible.length === 0) return false;
    return eligible.every((d: any) => Boolean(d.is_checked));
  }

  toggleSelectAll() {
    this.$emit("toggle-select-all", !this.isAllSelected);
  }

  get heightCalc() {
    const substractHeight = Number((this as any).heightOffset || 0);
    const hasSelected = (this.data || []).some((d: any) =>
      Boolean(d.is_checked)
    );
    const extra = hasSelected ? 40 : 0;
    const expansionAdjustment = this.isExpanded ? 0 : -80;
    return `calc(calc(43vh / var(--viewport-scale)) - ${substractHeight +
      extra +
      expansionAdjustment}px)`;
  }
}
